import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { setIITError } from '~/store/iit/slices.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { infoMessages } from '~/constants/infoMessages.ts';
import { saveSigningControllerFromIIT, startIITSigningController } from '~/store/iit/thunks.ts';
import { TIITSign } from '~/store/iit/types.ts';
import { resetIITLib } from '~/libs/iit/lib';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { previewDocumentRemoteBanking } from '~/store/remote-banking-contacts/thunks/documentPreviewRemoteBankingThunks.ts';
import { validateDocumentRemoteBankingContacts } from '~/store/remote-banking-contacts/thunks/validateDocumentRemoteBankingThunks.ts';
import { setRemoteBankingContactsError } from '~/store/remote-banking-contacts/slices.ts';
import {
  responsiblePersons,
  responsiblePersonsAccountManagers,
  responsiblePersonsInformation,
} from '~/store/remote-banking-contacts/thunks/responsiblePersonsThunks.ts';
import { verifyContrAgentRemoteBankingContacts } from '~/store/remote-banking-contacts/thunks/verifyContragentThunks.ts';
import { nameDocuments } from '~/modules/remote-banking-contacts/utils.ts';
import { processRepresentatives } from '~/store/remote-banking-contacts/thunks/processRepresentativesThunks.ts';
import { otpVerificationCheckRemoteBankingContacts } from '~/store/otp-verification/thunks/otpVerificationStatusThunks.ts';
import { setOtpError } from '~/store/otp-verification/slices.ts';
import { useNavigate } from 'react-router';
import { RootState } from '~/store/store.ts';
import { TClientUser, TUsersStore } from '~/store/users/types.ts';
import { PersonRole } from '~/modules/remote-banking-contacts/constants.tsx';
import { ERROR_CODES } from '~/constants/errorCodes.ts';

interface IApplicationSignatureProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  onSignatureSuccess: (successMessage: string) => void;
  setIsSubmittingForm: (value: boolean) => void;
  applicationDocumentId: string;
  onNext: () => void;
  current: number;
}

export const ApplicationSignatureRemoteBankingContacts = ({
  isModalOpen,
  handleCloseModal,
  onSignatureSuccess,
  setIsSubmittingForm,
  applicationDocumentId,
  current,
  onNext,
}: IApplicationSignatureProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token } = useSelector((state: RootState) => state.users as TUsersStore) || {};
  const { tokenData } = useSelector((state: RootState) => state.users.user as TClientUser) || {};
  const { applicationId } = tokenData || {};
  const { formPersons } = useSelector((state: RootState) => state.remoteBankingContacts);
  const { isLoading: IITLoading, error: IITError } =
    useSelector((state: RootState) => state.iit) || {};
  const { loading: isOTPLoading, error: OTPError } =
    useSelector((state: RootState) => state.otpVerification) || {};

  const isLoading = IITLoading || isOTPLoading;
  const error = IITError || OTPError;

  const isHeadOfDepartment = formPersons.every(
    (person) =>
      person.personRole === PersonRole.DIRECTOR || person.personRole === PersonRole.SOLE_TRADER
  );

  const closeAlertModal = () => {
    handleCloseModal();
    IITError && dispatch(setIITError(''));
    OTPError && dispatch(setOtpError(''));
    navigate('/services');
  };

  const documents = nameDocuments(current, applicationDocumentId);

  const closeModal = () => {
    handleCloseModal();
    IITError && dispatch(setIITError(''));
  };

  const handleSignatureIIT = async () => {
    if (isLoading) return;

    dispatch(
      startIITSigningController({
        path: `/remote-banking-contacts/applications/${applicationId}/documents/hashes`,
      })
    )
      .unwrap()
      .catch(() => {
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      });
  };

  const getResponsiblePersons = async () => {
    try {
      await dispatch(
        responsiblePersonsAccountManagers({ applicationId: applicationId!, token })
      ).unwrap();
      await dispatch(responsiblePersons({ applicationId: applicationId!, token })).unwrap();
      await dispatch(
        responsiblePersonsInformation({ applicationId: applicationId!, token })
      ).unwrap();

      onNext();
    } catch (err) {
      dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE));
    }
  };

  const getVerifyContrAgent = async () => {
    try {
      await dispatch(
        verifyContrAgentRemoteBankingContacts({ applicationId: applicationId!, token })
      ).unwrap();

      getResponsiblePersons();
    } catch (err: any) {
      if (err?.data?.errorCode === ERROR_CODES.COUNTERPARTY_ALREADY_EXISTS) {
        dispatch(setRemoteBankingContactsError(err?.data?.message));
      } else {
        dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE_TRY_AGAIN));
      }
    }
  };

  const validateDocument = async () => {
    try {
      const res = await dispatch(
        validateDocumentRemoteBankingContacts({
          applicationId: applicationId!,
          documentId: applicationDocumentId!,
          token,
        })
      ).unwrap();

      const { signatureValid, identityMatched } = res;

      if (signatureValid === false) {
        dispatch(setRemoteBankingContactsError(errorMessages.INVALID_SIGNATURE));
        return;
      }

      if (identityMatched === false) {
        dispatch(setRemoteBankingContactsError(errorMessages.DATA_DOES_NOT_MATCH));
        return;
      }

      if (signatureValid && identityMatched) {
        if (current === 2) {
          getVerifyContrAgent();
        } else {
          try {
            await dispatch(
              processRepresentatives({ applicationId: applicationId!, token })
            ).unwrap();
            if (isHeadOfDepartment) {
              await dispatch(
                otpVerificationCheckRemoteBankingContacts({ applicationId: applicationId!, token })
              );
            }

            const successMessage = isHeadOfDepartment
              ? infoMessages.DATA_SUCCESSFULLY_UPDATED
              : infoMessages.SEND_LETTER;

            handleSignatureSuccess(successMessage);
          } catch {
            dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE));
          }
        }
      }
    } catch {
      dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE));
    }
  };

  const onIITSign = async (signFiles: TIITSign[]) => {
    try {
      const res = await dispatch(
        saveSigningControllerFromIIT({
          fileList: signFiles,
          path: `/remote-banking-contacts/applications/${applicationId}/documents/signatures`,
        })
      )
        .unwrap()
        .catch(() => {
          dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
        });

      if (res) {
        closeModal();
        await validateDocument();
      } else {
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      }
    } catch (error) {
      dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
    } finally {
      resetIITLib();
    }
  };

  const handleSignatureSuccess = (successMessage: string): void => {
    onSignatureSuccess(successMessage);
    setIsSubmittingForm(false);
  };

  const handlePreview = async () => {
    await dispatch(
      previewDocumentRemoteBanking({
        applicationId: applicationId!,
        idDoc: applicationDocumentId!,
        token,
      })
    )
      .unwrap()
      .catch(() => {
        dispatch(setRemoteBankingContactsError(errorMessages.SERVICE_UNAVAILABLE));
      });
  };

  return (
    <>
      <ElectronicSignature
        isModalOpen={isModalOpen}
        handleCloseModal={closeModal}
        handleSignatureIIT={handleSignatureIIT}
        onIITSign={onIITSign}
        handlePreview={handlePreview}
        documents={documents}
        isDiiaSignatureDisabled
      />

      {isLoading && <FullPageLoader />}
      {!!error && <ModalAlert isOpen={!!error} text={error} onClose={closeAlertModal} />}
    </>
  );
};
